// ==========================================================
// Economy & Monetization Manager (Coins, VIP Pass, Unlocks)
// ==========================================================

export interface EconomyState {
  coins: number;
  isVIP: boolean;
  unlockedStages: number[]; // Array of unlocked stage IDs
  rewardAdsWatched: number;
  lastAdWatchTime: string | null;
}

const STORAGE_KEY = 'paint_meister_economy_v1';
const INITIAL_COINS = 500; // Free starter coins for new players

export class EconomyManager {
  private static getState(): EconomyState {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        // Initial State: Stages 1 to 5 are free by default
        const initialState: EconomyState = {
          coins: INITIAL_COINS,
          isVIP: false,
          unlockedStages: [1, 2, 3, 4, 5],
          rewardAdsWatched: 0,
          lastAdWatchTime: null
        };
        this.saveState(initialState);
        return initialState;
      }
      return JSON.parse(data);
    } catch {
      return {
        coins: INITIAL_COINS,
        isVIP: false,
        unlockedStages: [1, 2, 3, 4, 5],
        rewardAdsWatched: 0,
        lastAdWatchTime: null
      };
    }
  }

  private static saveState(state: EconomyState) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to persist economy state:', e);
    }
  }

  public static getCoins(): number {
    return this.getState().coins;
  }

  public static addCoins(amount: number): number {
    const state = this.getState();
    state.coins += Math.max(0, amount);
    this.saveState(state);
    return state.coins;
  }

  public static spendCoins(amount: number): boolean {
    const state = this.getState();
    if (state.coins >= amount) {
      state.coins -= amount;
      this.saveState(state);
      return true;
    }
    return false;
  }

  public static isVIP(): boolean {
    return this.getState().isVIP;
  }

  public static setVIP(active: boolean): void {
    const state = this.getState();
    state.isVIP = active;
    this.saveState(state);
  }

  public static isStageUnlocked(stageId: number, unlockCost: number = 0): boolean {
    const state = this.getState();
    // VIP unlocks all 100 stages immediately
    if (state.isVIP) return true;
    // Stages with 0 cost or stages 1-5 are always unlocked
    if (unlockCost === 0 || stageId <= 5) return true;
    return state.unlockedStages.includes(stageId);
  }

  public static unlockStage(stageId: number, cost: number): { success: boolean; message: string } {
    const state = this.getState();
    if (this.isStageUnlocked(stageId, cost)) {
      return { success: true, message: 'Already unlocked' };
    }

    if (state.coins >= cost) {
      state.coins -= cost;
      if (!state.unlockedStages.includes(stageId)) {
        state.unlockedStages.push(stageId);
      }
      this.saveState(state);
      return { success: true, message: 'Unlocked successfully' };
    }

    return { success: false, message: 'Not enough coins' };
  }

  public static claimRewardAd(): { coinsEarned: number; newTotal: number } {
    const state = this.getState();
    const rewardAmount = 500;
    state.coins += rewardAmount;
    state.rewardAdsWatched += 1;
    state.lastAdWatchTime = new Date().toISOString();
    this.saveState(state);
    return { coinsEarned: rewardAmount, newTotal: state.coins };
  }

  public static calculateStageReward(stars: number, totalScore: number): number {
    // Base 100 coins + 50 coins per star + score bonus
    let coins = 100 + stars * 50;
    if (totalScore >= 9500) coins += 100; // Grand Meister bonus
    if (this.isVIP()) coins = Math.floor(coins * 1.5); // VIP 1.5x coin boost
    return coins;
  }
}
